import type { CreateVehicleTO, UpdateVehicleTO } from '@control-center/control-center-client-public-vehicles-ts';
import Button from 'components/actions/Button';
import Dialog from 'components/overlays/Dialog';
import type { VehicleSummaryFE } from 'domain/vehicle';
import { type ReactElement, useState } from 'react';

import CreateVehicleForm from './CreateVehicleForm';
import UpdateVehicleForm from './UpdateVehicleForm';

export type VehicleDialogSubmit =
    | { mode: 'create'; vehicle: CreateVehicleTO }
    | { mode: 'update'; vin: string; vehicle: UpdateVehicleTO };

interface VehicleDialogProps {
    show: boolean;
    isSubmitting: boolean;
    mode: 'create' | 'update';
    vehicle?: VehicleSummaryFE;
    onCancel: () => void;
    onSubmit: (payload: VehicleDialogSubmit) => void | Promise<void>;
}

const VehicleDialog = (props: VehicleDialogProps) => {
    const { show, mode, vehicle, isSubmitting, onCancel, onSubmit } = props;
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [activeFormDirty, setActiveFormDirty] = useState(false);
    const targetKey = mode === 'update' && vehicle ? `${mode}-${vehicle.vin}` : mode;

    const requestCancel = () => {
        if (isSubmitting) {
            return;
        }
        if (activeFormDirty) {
            setShowExitConfirm(true);
        } else {
            onCancel();
        }
    };

    const confirmExit = () => {
        setShowExitConfirm(false);
        onCancel();
    };

    const dismissExit = () => {
        setShowExitConfirm(false);
    };

    const validateClose = () => {
        if (isSubmitting) {
            return false;
        }
        if (activeFormDirty) {
            requestCancel();
            return false;
        }
        return true;
    };

    const renderMainDialog = (body: ReactElement) => (
        <Dialog
            data-testid='vehicle-dialog'
            show={show && !showExitConfirm}
            title={<p>{mode === 'update' ? 'Edit vehicle information' : 'Create new vehicle'}</p>}
            showCloseButton
            onClose={onCancel}
            onCloseValidation={validateClose}
            disableEsc={isSubmitting}
            body={body}
        />
    );

    return (
        <>
            {mode === 'update' && vehicle ? (
                <UpdateVehicleForm
                    key={targetKey}
                    show={show}
                    vehicle={vehicle}
                    isSubmitting={isSubmitting}
                    onUpdate={(vin, updatedVehicle) => onSubmit({ mode: 'update', vin, vehicle: updatedVehicle })}
                    requestCancel={requestCancel}
                    onDirtyChange={setActiveFormDirty}
                    renderDialog={renderMainDialog}
                />
            ) : (
                <CreateVehicleForm
                    key={targetKey}
                    show={show}
                    isSubmitting={isSubmitting}
                    onCreate={createdVehicle => onSubmit({ mode: 'create', vehicle: createdVehicle })}
                    requestCancel={requestCancel}
                    onDirtyChange={setActiveFormDirty}
                    renderDialog={renderMainDialog}
                />
            )}
            <Dialog
                data-testid='vehicle-dialog-exit-confirm'
                show={showExitConfirm}
                title='Are you sure you want to cancel?'
                showCloseButton
                onClose={dismissExit}
                body={<p>Your changes will be lost and we won't be able to recover your inputs.</p>}
                footer={
                    <div className='display-flex justify-content-end gap-10'>
                        <Button data-testid='vehicle-dialog-exit-confirm-back' onClick={dismissExit}>
                            No, back to editing
                        </Button>
                        <Button
                            className='btn btn-danger'
                            data-testid='vehicle-dialog-exit-confirm-cancel'
                            onClick={confirmExit}
                        >
                            Yes, cancel
                        </Button>
                    </div>
                }
            />
        </>
    );
};

export default VehicleDialog;
